import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, Menu, Portal } from "@headlessui/react";
import { AdjustmentsIcon, CheckIcon, DotsVerticalIcon } from "@heroicons/react/outline";
import { motion, useReducedMotion } from "framer-motion";
import JobPricingAdjustmentWizard from "./JobPricingAdjustmentWizard";

const labels = { name: "Name", customer_amount: "Customer price", vendor_amount: "Vendor price" };
const labelsFor = (row) => row?.kind === "service" ? { name: labels.name, scope: "Scope of work", customer_amount: labels.customer_amount, vendor_amount: labels.vendor_amount } : labels;
const overrideFlag = (field) => field === "scope" ? "hasScopeOverride" : field === "name" ? "hasNameOverride" : "hasAmountOverride";
const money = (value) => Number(value || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const rowKey = (row) => `${row.kind}:${row.id}`;
const valueFor = (row, field, defaults = false) => field === "scope"
  ? row.customer?.[defaults ? "defaultScope" : "scope"] ?? row.vendor?.[defaults ? "defaultScope" : "scope"] ?? row.scope ?? ""
  : field === "name"
  ? row.customer?.[defaults ? "defaultName" : "displayName"] || row.name
  : row[field === "customer_amount" ? "customer" : "vendor"]?.[defaults ? "defaultInputValue" : "inputValue"]
    ?? row[field === "customer_amount" ? "customer" : "vendor"]?.[defaults ? "defaultAmount" : "amount"] ?? "";

export function OverrideMark({ active }) {
  return active ? <AdjustmentsIcon className="inline h-3.5 w-3.5 shrink-0 align-middle text-sky-600" role="img" aria-hidden={false} aria-label="Manually changed for this job" title="This value was manually changed for this job." /> : null;
}

// One active row and one request at a time: a slower response cannot overwrite newer typing.
export function useJobPricingEditor({ enabled, revision, onSave, adjustmentTypes, showPriceOverrides = true, fixedFeesOnly = false }) {
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const reduceMotion = useReducedMotion();
  const [restore, setRestore] = useState(null);
  const [remove, setRemove] = useState(null);
  const [addKind, setAddKind] = useState(null);
  const [selected, setSelected] = useState([]);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const draftRef = useRef(draft);
  const activeRef = useRef(active);
  const busy = useRef(false);
  const revisionRef = useRef(revision);
  useEffect(() => { revisionRef.current = revision; }, [revision]);
  useEffect(() => {
    if (!saveResult) return;
    const timer = setTimeout(() => setSaveResult(null), 2500);
    return () => clearTimeout(timer);
  }, [saveResult]);
  draftRef.current = draft;
  activeRef.current = active;
  const dirty = Object.keys(draft).length > 0;
  const valid = Object.entries(draft).every(([field, value]) => field === "scope" ? typeof value === "string" : field === "name"
    ? value.trim().length > 0 && value.trim().length <= 255
    : /^\d+(\.\d{1,2})?$/.test(value) && Number(value) <= 9999999.99);

  const persist = useCallback(async (row, changes, options = {}) => {
    if (busy.current) return false;
    busy.current = true;
    setSaving(true);
    setError("");
    setSaveResult(null);
    try {
      const result = await onSave({ kind: row.kind, ...(row.id !== undefined ? { row_id: row.id } : {}), revision: revisionRef.current, ...(changes ? { changes } : {}), ...options });
      revisionRef.current = result.pricingRevision;
      setSaved(true);
      setSaveResult({ key: rowKey(row), success: true });
      return true;
    } catch (err) {
      const message = err.response?.data?.error;
      setError(message || "Pricing could not be saved. Your changes are still here; please retry.");
      setSaveResult({ key: rowKey(row), success: false });
      return false;
    } finally {
      busy.current = false;
      setSaving(false);
    }
  }, [onSave]);

  const flush = useCallback(async () => {
    if (busy.current || !activeRef.current || !Object.keys(draftRef.current).length) return;
    const submitted = { ...draftRef.current };
    if (await persist(activeRef.current, submitted)) {
      setDraft((current) => Object.fromEntries(Object.entries(current).filter(([field, value]) => submitted[field] !== value)));
    }
  }, [persist]);

  useEffect(() => {
    if (!dirty || !valid || saving || error) return;
    const timer = setTimeout(flush, 600);
    return () => clearTimeout(timer);
  }, [dirty, valid, draft, saving, error, flush]);

  useEffect(() => {
    if (!dirty && !saving) return;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    // Keep SPA links from silently abandoning an unsaved pricing change.
    const guardLink = (event) => {
      if (event.target.closest?.("a[href]") && !window.confirm("Pricing changes are not saved yet. Leave this page?")) {
        event.preventDefault(); event.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", warn);
    document.addEventListener("click", guardLink, true);
    return () => { window.removeEventListener("beforeunload", warn); document.removeEventListener("click", guardLink, true); };
  }, [dirty, saving]);

  const cell = (row, field, children) => {
    const data = field === "vendor_amount" ? row.vendor : row.customer;
    const modified = (field === "name" || showPriceOverrides) &&
      data?.[field === "name" ? "nameOverridden" : "amountOverridden"];
    if (!enabled || rowKey(active || {}) !== rowKey(row)) {
      if (!modified) return children;
      return field === "name"
        ? <>{children} <OverrideMark active /></>
        : <span className="inline-flex items-center gap-1 whitespace-nowrap"><OverrideMark active /><span>{children}</span></span>;
    }
    const percentage = field !== "name" && data?.inputIsPercentage;
    const inputField = percentage ? field.replace("_amount", "_percentage") : field;
    const value = Object.prototype.hasOwnProperty.call(draft, inputField) ? draft[inputField] : valueFor(row, field);
    return <span className="inline-flex w-full min-w-0 flex-col">
      <span className="inline-flex w-full min-w-0 items-center gap-1">
      {field !== "name" && !percentage && <span aria-hidden="true">$</span>}
      <input aria-label={`${row.name} ${labels[field]}${percentage ? " percentage" : ""}`} type="text" inputMode={field === "name" ? "text" : "decimal"}
        value={value} maxLength={field === "name" ? 255 : 12}
        onChange={(event) => { setDraft((current) => ({ ...current, [inputField]: event.target.value })); setSaved(false); }}
        className={`min-w-0 w-full rounded-md border border-sky-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-sky-500 focus:ring-sky-500 ${field === "name" ? "" : "text-right"}`} />
      <OverrideMark active={modified} />
      {percentage && <span aria-hidden="true">%</span>}
      </span>
      {percentage && (
        <span className="mt-1 text-right text-xs font-normal text-slate-500">{money(data?.amount)}</span>
      )}
    </span>;
  };

  const isEditing = (row) => enabled && rowKey(active || {}) === rowKey(row);
  const scope = (row, children) => !isEditing(row) ? children : (
    <div className="mt-3 min-w-0">
      <label htmlFor={`job-service-scope-${row.id}`} className="flex items-center gap-1 text-xs font-semibold text-slate-600">
        Scope of work <OverrideMark active={row.customer?.scopeOverridden} />
      </label>
      <textarea id={`job-service-scope-${row.id}`} aria-label={`${row.name} Scope of work`} rows={5}
        value={Object.prototype.hasOwnProperty.call(draft, "scope") ? draft.scope : valueFor(row, "scope")}
        onChange={(event) => { setDraft((current) => ({ ...current, scope: event.target.value })); setSaved(false); }}
        className="mt-1 block w-full min-w-0 rounded-md border border-sky-300 bg-white px-3 py-2 text-sm font-normal leading-6 text-slate-700 shadow-sm focus:border-sky-500 focus:ring-sky-500" />
      <p className="mt-1 text-xs font-normal text-slate-500">Applies only to this job. Changes save automatically.</p>
    </div>
  );
  const actions = (row) => !enabled ? null : (
    <Menu as="div" className="relative justify-self-end">
      <Menu.Button aria-label={`Pricing options for ${row.name}`} disabled={dirty || saving}
        onClick={(event) => { const rect = event.currentTarget.getBoundingClientRect(); setMenuPosition({ top: Math.max(8, Math.min(rect.bottom + 4, window.innerHeight - 140)), left: Math.max(8, Math.min(rect.right - 144, window.innerWidth - 152)) }); }}
        className="rounded-md p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-40">
        <DotsVerticalIcon className={`h-5 w-5 ${saveResult?.key === rowKey(row) ? "opacity-0" : ""}`} />
      </Menu.Button>
      {saveResult?.key === rowKey(row) && <motion.span
        role="img" aria-label={`${saveResult.success ? "Pricing saved" : "Pricing save failed"} for ${row.name}`}
        initial={reduceMotion ? false : { opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
        className={`pointer-events-none absolute right-0 top-0 flex h-7 w-7 items-center justify-center rounded-full border shadow-sm ${saveResult.success ? "border-emerald-600 bg-emerald-600 text-white" : "border-red-300 bg-red-50 text-red-600"}`}>
        <CheckIcon className="h-4 w-4" strokeWidth={3} aria-hidden="true" />
      </motion.span>}
      <Portal>
        <Menu.Items style={menuPosition} className="fixed z-[12000] w-36 rounded-lg border border-slate-200 bg-white p-1 text-sm shadow-lg focus:outline-none">
          <Menu.Item>{({ active: focused }) => <button className={`block w-full rounded px-3 py-2 text-left ${focused ? "bg-slate-100" : ""}`} onClick={() => { setActive(isEditing(row) ? null : row); setDraft({}); setError(""); setSaved(false); }}>{isEditing(row) ? "Close edit" : "Edit"}</button>}</Menu.Item>
          <Menu.Item>{({ active: focused }) => <button className={`block w-full rounded px-3 py-2 text-left ${focused ? "bg-slate-100" : ""}`} onClick={() => { setRestore(row); setError(""); setSelected(Object.keys(labelsFor(row)).filter((field) => (field === "vendor_amount" ? row.vendor : row.customer)?.[overrideFlag(field)])); }}>Restore</button>}</Menu.Item>
          {row.kind !== "service" && <Menu.Item>{({ active: focused }) => <button className={`block w-full rounded px-3 py-2 text-left text-red-700 ${focused ? "bg-red-50" : ""}`} onClick={() => { setRemove(row); setError(""); }}>Remove</button>}</Menu.Item>}
        </Menu.Items>
      </Portal>
    </Menu>
  );

  const feedback = <>
    {enabled && (active || restore) && <div className="border-t border-slate-200 px-5 py-3 text-xs text-slate-500">
      <p>Changes apply only to this job. Percentage rows save the rate; fixed rows save the dollar amount.</p>
      <p role={error ? "alert" : "status"} className={`mt-1 ${error || (dirty && !valid) ? "text-red-700" : "text-slate-600"}`}>
        {error || (dirty && !valid ? "Enter a name and non-negative amounts with up to two decimal places." : saving ? "Saving pricing…" : dirty ? "Changes pending…" : saved ? "Pricing saved." : "Changes save automatically.")}
        {error && dirty && <button className="ml-2 font-semibold underline" disabled={!valid || saving} onClick={flush}>Retry</button>}
      </p>
    </div>}
    <Dialog open={Boolean(restore)} onClose={() => { if (!saving) setRestore(null); }} className="relative z-[12000]">
      <div className="fixed inset-0 bg-slate-900/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-4">
        <Dialog.Panel className="w-full max-w-xl rounded-xl border border-slate-200 bg-white p-5 text-sm shadow-xl">
          <Dialog.Title className="text-lg font-bold text-slate-950">Restore row defaults</Dialog.Title>
          <Dialog.Description className="mt-1 text-slate-500">{restore?.kind === "service" ? "Choose which fields to restore to the service catalog and current mapped pricing." : "Choose which cells to restore to the current mapped pricing and default fee rules."} Unchecked cells will stay unchanged.</Dialog.Description>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {restore && Object.entries(labelsFor(restore)).map(([field, label]) => <label key={field} className={`min-w-0 rounded-lg border border-slate-200 p-3 ${field === "name" || field === "scope" ? "col-span-2" : ""}`}>
              <span className="flex items-center gap-2 font-semibold text-slate-700"><input type="checkbox" checked={selected.includes(field)} disabled={saving} onChange={(event) => setSelected((current) => event.target.checked ? [...current, field] : current.filter((item) => item !== field))} className="rounded border-slate-300 text-sky-600 focus:ring-sky-500" />{label}</span>
              <span className={`mt-2 block break-words text-slate-500 ${field === "scope" ? "max-h-40 overflow-y-auto whitespace-pre-wrap" : ""}`}>{field === "name" || field === "scope" ? valueFor(restore, field, true) : (field === "customer_amount" ? restore.customer : restore.vendor)?.defaultInputIsPercentage ? `${valueFor(restore, field, true)}%` : money(valueFor(restore, field, true))}</span>
            </label>)}
          </div>
          {error && <p role="alert" className="mt-3 text-red-700">{error}</p>}
          <div className="mt-5 flex justify-end gap-2">
            <button disabled={saving} onClick={() => setRestore(null)} className="rounded-lg border border-slate-200 bg-slate-100 px-4 py-2 font-semibold text-slate-700">Cancel</button>
            <button disabled={saving || selected.length === 0} onClick={async () => {
              if (await persist(restore, Object.fromEntries(selected.map((field) => [field, null])))) { setRestore(null); setDraft({}); }
            }} className="rounded-lg bg-[#E3222A] px-4 py-2 font-semibold text-white disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
    <Dialog open={Boolean(remove)} onClose={() => { if (!saving) setRemove(null); }} className="relative z-[12000]">
      <div className="fixed inset-0 bg-slate-900/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-4">
        <Dialog.Panel className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 text-sm shadow-xl">
          <Dialog.Title className="text-lg font-bold text-slate-950">Remove {remove?.kind}?</Dialog.Title>
          <Dialog.Description className="mt-2 break-words text-slate-600">Remove {remove?.name} from both customer and vendor pricing for this job only. Customer settings and other jobs will not change.</Dialog.Description>
          {error && <p role="alert" className="mt-3 text-red-700">{error}</p>}
          <div className="mt-5 flex justify-end gap-2">
            <button disabled={saving} onClick={() => setRemove(null)} className="rounded-lg border border-slate-200 px-4 py-2">Cancel</button>
            <button disabled={saving} onClick={async () => { if (await persist(remove, null, { action: "remove" })) { setRemove(null); setActive(null); setDraft({}); } }} className="rounded-lg bg-[#E3222A] px-4 py-2 font-semibold text-white disabled:opacity-50">{saving ? "Removing…" : "Remove"}</button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
    {addKind && <JobPricingAdjustmentWizard kind={addKind} types={adjustmentTypes?.[addKind] || []} saving={saving} error={error} fixedFeesOnly={fixedFeesOnly}
      onClose={() => { if (!saving) setAddKind(null); }}
      onSave={async ({ changes, ...options }) => { if (await persist({ kind: addKind }, changes, { action: "add", ...options })) { setAddKind(null); setActive(null); setDraft({}); } }} />}
  </>;
  const addButton = (kind) => enabled && adjustmentTypes && (!fixedFeesOnly || kind === "fee") ? <button disabled={dirty || saving} onClick={() => { setAddKind(kind); setError(""); }} className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold normal-case tracking-normal text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50">+ Add {kind}</button> : null;
  return { cell, scope, actions, feedback, isEditing, addButton };
}
